package features.members.handlers

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import AppState
import Router
import shared.ui.ModalManager
import features.members.domain.Member
import features.members.data.MemberRepository

/**
 * Handler for adding a new member to the team
 */
fun addMember() {
    console.log("🔧 addMember() called")
    
    val teamId = AppState.currentTeam?.id
    if (teamId == null) {
        window.alert("No team selected")
        return
    }
    
    val emailInput = document.getElementById("member-email-input") as? HTMLInputElement
    val roleInput = document.getElementById("member-role-input") as? HTMLSelectElement
    
    val email = emailInput?.value?.trim() ?: ""
    val role = roleInput?.value ?: "member"
    
    if (email.isEmpty()) {
        window.alert("Please enter an email address")
        return
    }
    
    // Check if validation passed
    val validationResult = features.auth.validation.currentValidationResult
    if (validationResult == null || !validationResult.exists || validationResult.user == null) {
        window.alert("Please enter a valid registered user email")
        return
    }
    
    val validatedUser = validationResult.user!!
    
    // Create member from validated user
    val newMember = Member(
        id = validatedUser.id,
        name = validatedUser.name,
        email = validatedUser.email,
        role = role,
        joinDate = getCurrentDate(),
        quota = 12
    )
    
    console.log("Adding member:", newMember.name, "to team:", teamId)
    
    MemberRepository.addMember(teamId, newMember)
        .then {
            console.log("✅ Member added successfully")
            
            // Reload members
            MemberRepository.loadMembers(teamId)
                .then { members ->
                    AppState.members[teamId] = members.toMutableList()
                    
                    // Clear inputs and validation
                    emailInput?.value = ""
                    roleInput?.value = "member"
                    features.auth.validation.hideValidationMessage()
                    features.auth.validation.currentValidationResult = null
                    
                    // Close modal and re-render
                    ModalManager.close()
                    Router.render()
                }
        }
        .catch { error ->
            console.error("❌ Error adding member:", error)
            window.alert("Failed to add member. Please try again.")
        }
}

/**
 * Handler for saving changes to an existing member
 */
fun saveMemberChanges() {
    console.log("🔧 saveMemberChanges() called")
    
    val teamId = AppState.currentTeam?.id
    if (teamId == null) {
        window.alert("No team selected")
        return
    }
    
    val memberIdInput = document.getElementById("edit-member-id") as? HTMLInputElement
    val nameInput = document.getElementById("edit-member-name") as? HTMLInputElement
    val emailInput = document.getElementById("edit-member-email") as? HTMLInputElement
    val roleInput = document.getElementById("edit-member-role") as? HTMLSelectElement
    
    val memberId = memberIdInput?.value ?: ""
    val name = nameInput?.value?.trim() ?: ""
    val email = emailInput?.value?.trim() ?: ""
    val role = roleInput?.value ?: "member"
    
    if (memberId.isEmpty() || name.isEmpty() || email.isEmpty()) {
        window.alert("Please fill in all fields")
        return
    }
    
    console.log("Updating member:", memberId, "in team:", teamId)
    
    MemberRepository.updateMember(teamId, memberId, role)
        .then {
            console.log("✅ Member updated successfully")
            
            // Reload members
            MemberRepository.loadMembers(teamId)
                .then { members: Array<Member> ->
                    AppState.members[teamId] = members.toMutableList()
                    
                    // Close modal and re-render
                    ModalManager.close()
                    Router.render()
                }
        }
        .catch { error ->
            console.error("❌ Error updating member:", error)
            window.alert("Failed to update member. Please try again.")
        }
}

/**
 * Handler for removing a member from the team
 */
fun removeMember() {
    console.log("🔧 removeMember() called")
    
    val teamId = AppState.currentTeam?.id
    if (teamId == null) {
        window.alert("No team selected")
        return
    }
    
    val memberIdInput = document.getElementById("edit-member-id") as? HTMLInputElement
    val memberId = memberIdInput?.value ?: ""
    
    if (memberId.isEmpty()) {
        window.alert("No member selected")
        return
    }
    
    val member = AppState.members[teamId]?.find { it.id == memberId }
    if (member == null) {
        window.alert("Member not found")
        return
    }
    
    if (!window.confirm("Are you sure you want to remove ${member.name} from the team?")) {
        return
    }
    
    console.log("Removing member:", memberId, "from team:", teamId)
    
    MemberRepository.removeMember(teamId, memberId)
        .then {
            console.log("✅ Member removed successfully")
            
            // Reload members
            MemberRepository.loadMembers(teamId)
                .then { members ->
                    AppState.members[teamId] = members.toMutableList()
                    
                    // Close modal and re-render
                    ModalManager.close()
                    Router.render()
                }
        }
        .catch { error ->
            console.error("❌ Error removing member:", error)
            window.alert("Failed to remove member. Please try again.")
        }
}

// Helper functions
private fun generateId(): String {
    return js("Date.now().toString() + Math.random().toString(36).substr(2, 9)") as String
}

private fun getCurrentDate(): String {
    return js("new Date().toISOString().split('T')[0]") as String
}
